import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import './index.css';
import { API_ENDPOINTS } from './config';

function AvatarDisplay() {
  const { visitorId, avatarId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const sessionId = location.state?.sessionId;
  const capturedImage = location.state?.capturedImage;
  const [avatarData, setAvatarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(30); // 30 seconds in seconds
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const whatsappMessageSentRef = useRef(false); // Track if WhatsApp message was already sent
  
  // Redirect to AvatarCreation if no capturedImage (e.g., on page refresh)
  useEffect(() => {
    if (!capturedImage) {
      console.log('No capturedImage found - redirecting to AvatarCreation');
      setShouldRedirect(true);
      const timer = setTimeout(() => {
        navigate('/avatar', { replace: true });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [capturedImage, navigate]);
  
  // Send WhatsApp message after avatar is ready
  const sendWhatsAppMessage = async (visitorId, sessionId) => {
    // Prevent duplicate calls
    if (whatsappMessageSentRef.current) {
      console.log('WhatsApp message already sent, skipping');
      return;
    }
    
    if (!visitorId || !sessionId) {
      console.log('Cannot send WhatsApp message: missing visitorId or sessionId');
      return;
    }
    
    // Mark as sent immediately to prevent duplicate calls
    whatsappMessageSentRef.current = true;
    
    try {
      console.log('Sending WhatsApp message for visitor:', visitorId);
      
      const response = await fetch(API_ENDPOINTS.sendWhatsAppMessage(visitorId), {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'session-id': sessionId,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ templates: ['ai_avatar', 'brochures'] }),
      });
      
      const data = await response.json();
      console.log('WhatsApp message API response:', data);
      
      if (data.error === false) {
        console.log('WhatsApp message sent successfully');
      } else {
        console.error('Failed to send WhatsApp message:', data.message);
        // Reset the flag on failure so it can be retried
        whatsappMessageSentRef.current = false;
      }
    } catch (err) {
      console.error('Error sending WhatsApp message:', err);
      // Reset the flag on failure so it can be retried
      whatsappMessageSentRef.current = false;
    }
  };
  
  // Show redirect message if needed
  if (shouldRedirect) {
    return (
      <div className="avatar-creation-page">
        <div className="avatar-creation-container">
          <div className="logo-section">
            <img 
              src="/AeonX Logo.svg" 
              alt="AeonX Logo" 
              className="aeonx-logo"
            />
          </div>
          <h1 className="event-name">India AI Impact Summit 2026</h1>
          <div className="generating-message">
            <p>Redirecting to create your avatar...</p>
          </div>
        </div>
      </div>
    );
  }

  useEffect(() => {
    const fetchAvatar = async () => {
      try {
        const { url: avatarUrl, sessionId: storedSessionId } = API_ENDPOINTS.avatarFetch(visitorId, avatarId);
        
        const headers = {
          'accept': 'application/json',
          'session-id': storedSessionId || sessionId,
        };
        
        const response = await fetch(avatarUrl, {
          method: 'GET',
          headers,
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch avatar');
        }
        
        const data = await response.json();
        console.log('Avatar API response:', data);
        
        if (data.error === false && data.data) {
          setAvatarData(data.data);
          
          // Send WhatsApp message after avatar is successfully loaded
          sendWhatsAppMessage(visitorId, sessionId);
        } else {
          setError(data.message || 'Failed to load avatar');
        }
      } catch (err) {
        console.error('Error fetching avatar:', err);
        setError('Failed to load avatar. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchAvatar();
    
    // Countdown timer to update time left every second
    const countdownInterval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          // Time's up - navigate to main app page
          navigate('/');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    // Cleanup timers on unmount
    return () => {
      clearInterval(countdownInterval);
    };
  }, [visitorId, avatarId, sessionId, navigate]);
  
  // Format time left as MM:SS
  const formatTimeLeft = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="avatar-creation-page">
        <div className="avatar-creation-container">
          <div className="logo-section">
            <img 
              src="/AeonX Logo.svg" 
              alt="AeonX Logo" 
              className="aeonx-logo"
            />
          </div>

          <h1 className="event-name">India AI Impact Summit 2026</h1>

          <div className="generating-message">
            <div className="spinner"></div>
            <p>Loading your avatar...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="avatar-creation-page">
        <div className="avatar-creation-container">
          <div className="logo-section">
            <img 
              src="/AeonX Logo.svg" 
              alt="AeonX Logo" 
              className="aeonx-logo"
            />
          </div>

          <h1 className="event-name">India AI Impact Summit 2026</h1>

          <div className="error-message">
            {error}
          </div>

          <Link to="/avatar" className="create-avatar-btn" style={{
            marginTop: '20px'
          }}>
            Create New Avatar
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="avatar-creation-page" style={{ 
      minHeight: '100vh',
      height: '100%',
      minWidth: '100%',
      overflowY: 'auto',
      overflowX: 'hidden',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '24px',
      position: 'relative'
    }}>
      <div className="avatar-creation-container" style={{ maxWidth: '500px', width: '100%' }}>
        {/* AeonX Logo */}
        <div className="logo-section">
          <img 
            src="/AeonX Logo.svg" 
            alt="AeonX Logo" 
            className="aeonx-logo"
          />
        </div>

        {/* Event Name */}
        <h1 className="event-name">India AI Impact Summit 2026</h1>

        {/* Success Message */}
        <p className="instructions">
          Your AI Avatar is ready!
        </p>

        {/* Countdown Timer */}
        <p className="countdown-timer">
          Redirecting in {formatTimeLeft(timeLeft)}
        </p>

        {/* Success Message */}
        <p className="instructions">
          Your AI Avatar is ready!
        </p>

        {/* Avatar Display */}
        <div className="avatar-comparison">
          {capturedImage && (
            <div className="avatar-comparison-item">
              <h3 className="comparison-title">Original Photo</h3>
              <div className="camera-area">
                <div className="captured-photo">
                  <img 
                    src={capturedImage} 
                    alt="Your Photo" 
                    className="captured-image"
                  />
                </div>
              </div>
            </div>
          )}
          
          <div className="avatar-comparison-item">
            <h3 className="comparison-title">AI Generated Avatar</h3>
            <div className="camera-area">
              <div className="captured-photo">
                {avatarData?.signed_url ? (
                  <img 
                    src={avatarData.signed_url} 
                    alt="Your AI Avatar" 
                    className="captured-image"
                  />
                ) : avatarData?.avatar_url ? (
                  <img 
                    src={avatarData.avatar_url} 
                    alt="Your AI Avatar" 
                    className="captured-image"
                  />
                ) : (
                  <div className="avatar-placeholder">
                    <div className="placeholder-icon">🤖</div>
                    <p>Avatar Preview</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AvatarDisplay;
